import moment from "moment";
import { useContext } from "react";

import { UserContext } from "@/components/User";
import { diffUntilNow } from "@/core/dates";
import { EOriginSystemOwner } from "@/data/job-requests/types/JobRequestOrigin";

import { EJobRequestStatus, IJobRequest } from "../types-ensure";

interface UseJobRequestApplyAvailabilityReturn {
  isExpired: boolean;
  isMember: boolean;
  isVatInvalid: boolean;
  isForCompanies: boolean;
  isApplyAvailable: boolean;
  isVatRequired: boolean;
  isOrgInvalid: boolean;
}

export const useJobRequestApplyAvailability = (jr?: IJobRequest): UseJobRequestApplyAvailabilityReturn => {
  const { contextConsultantProfile, contextCompany, hasUserRoleInContextCompany, hasCompanyManagerPrivileges } =
    useContext(UserContext);

  if (!jr) {
    return {
      isExpired: false,
      isMember: false,
      isVatInvalid: false,
      isForCompanies: false,
      isApplyAvailable: false,
      isVatRequired: false,
      isOrgInvalid: false,
    };
  }

  const isTooLateToApply =
    jr.lastDayOfApplications &&
    moment(jr.lastDayOfApplications).isValid() &&
    diffUntilNow(jr.lastDayOfApplications, "second") <= 0;

  const isExpired = isTooLateToApply || jr.status === EJobRequestStatus.Closed;

  const isMember = hasUserRoleInContextCompany?.("CONSULTANT");

  const isManager = hasCompanyManagerPrivileges?.();

  const isVatInvalid = !contextCompany?.validVatNumber && isManager;

  const isOrgInvalid = !contextCompany?.validOrgNumber && isManager;

  const isForCompanies =
    Boolean(contextConsultantProfile) && Boolean(!contextCompany) && jr.origin.systemOwner == EOriginSystemOwner.VMS;

  const isVatRequired =
    jr.origin.systemOwner == EOriginSystemOwner.VMS && (contextCompany?.vatNo == "" || contextCompany?.vatNo == null);

  const isApplyAvailable =
    !isExpired && !isMember && !isVatInvalid && !isForCompanies && !isVatRequired && !isOrgInvalid;

  return {
    isExpired,
    isMember,
    isVatInvalid,
    isForCompanies,
    isApplyAvailable,
    isVatRequired,
    isOrgInvalid,
  };
};
